import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  Quote,
  Building2,
  Users,
  TrendingUp,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const testimonials = [
  {
    name: "Sarah Mitchell",
    initials: "SM",
    role: "Head of Talent Acquisition",
    company: "TechCorp Solutions",
    size: "500,1000 employees",
    accent: C.coral,
    quote:
      "WebIdeal transformed our hiring. Time-to-hire dropped 60% and AI screening saved hundreds of hours. One platform, one record, no more juggling tools.",
    results: ["60% faster hiring", "500+ hrs saved/mo", "85% candidate satisfaction"],
  },
  {
    name: "James Rodriguez",
    initials: "JR",
    role: "CEO & Founder",
    company: "StartupXYZ",
    size: "10,50 employees",
    accent: C.mint,
    quote:
      "Enterprise-grade recruiting without the enterprise price tag. The AI resume parsing alone is worth the subscription.",
    results: ["3× more quality candidates", "$15K saved annually", "Hired 10 in 2 months"],
  },
  {
    name: "Priya Sharma",
    initials: "PS",
    role: "Director of HR",
    company: "Global Staffing Inc",
    size: "1000+ employees",
    accent: C.gold,
    quote:
      "The HRMS integration changed everything. One source of truth from candidate to employee. The analytics are insights we never had before.",
    results: ["Unified data · 5 countries", "70% admin reduction", "Real-time compliance"],
  },
  {
    name: "Michael Chen",
    initials: "MC",
    role: "Recruiting Manager",
    company: "FinTech Ventures",
    size: "200,500 employees",
    accent: C.coral,
    quote:
      "AI candidate matching is incredibly accurate. Better fits, faster retention is up 40%, and ROI was clear within the first quarter.",
    results: ["40% better retention", "95% match accuracy", "Positive ROI · Q1"],
  },
  {
    name: "Emma Williams",
    initials: "EW",
    role: "Talent Partner",
    company: "Creative Agency Co",
    size: "50,100 employees",
    accent: C.mint,
    quote:
      "Candidate experience is phenomenal. Applicants love the chatbot and the automated updates. Application completion is up 50%.",
    results: ["50% higher completion", "4.8/5 candidate rating", "Zero pipeline drop-offs"],
  },
  {
    name: "David Park",
    initials: "DP",
    role: "VP of Operations",
    company: "Manufacturing Plus",
    size: "500,1000 employees",
    accent: C.gold,
    quote:
      "Finally, a platform that gets blue-collar hiring. Mobile-first plus multi-language let us hire across three countries seamlessly.",
    results: ["Hiring · 3 countries", "Mobile-first wins", "Multi-language stack"],
  },
];

const stats = [
  { value: "500+", label: "Companies trust us", icon: Building2 },
  { value: "50K+", label: "Candidates placed",  icon: Users     },
  { value: "60%",  label: "Avg. time saved",    icon: TrendingUp },
];

export default function Testimonials() {
  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Customers · {testimonials.length} stories</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Words from
              <br />
              <span style={{ color: `${C.forest}80` }}>real teams.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Six teams, six stacks, six honest reads on what changed when WebIdeal
              landed in their hiring process.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* STATS */}
      <section className="py-12 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {stats.map((s, i) => (
              <div key={s.label} className="p-7 md:p-8 flex items-center gap-5" style={{ background: C.paper }}>
                <div className="w-12 h-12 flex items-center justify-center" style={{ background: C.forest, color: C.paper }}>
                  <s.icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div>
                  <span className="text-3xl md:text-4xl font-extrabold tracking-tight tabular-nums" style={{ color: C.forest }}>
                    {s.value}
                  </span>
                  <p className="text-xs mt-1 font-medium" style={{ color: C.grid }}>
                    {s.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* TESTIMONIALS GRID */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>The receipts</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: C.forest }}>
                Outcomes, in their own words.
              </h2>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>06 cohorts · validated</TechLabel>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: C.hairline }}>
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="ds-spatial relative p-7 md:p-8 transition-colors duration-200 hover:bg-white"
                style={{ background: C.paper, borderLeft: `4px solid ${t.accent}` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <Quote className="w-7 h-7" style={{ color: t.accent }} strokeWidth={1.4} />
                  <TechLabel style={{ color: t.accent }}>{t.id}</TechLabel>
                </div>

                <div className="flex gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-3.5 h-3.5" style={{ color: t.accent, fill: t.accent }} />
                  ))}
                </div>

                <blockquote className="text-base leading-relaxed" style={{ color: C.forest }}>
                  "{t.quote}"
                </blockquote>

                {/* Results chips */}
                <div className="mt-5 flex flex-wrap gap-px" style={{ background: C.hairline }}>
                  {t.results.map((r) => (
                    <span
                      key={r}
                      className="text-[10px] font-bold uppercase px-2.5 py-1.5"
                      style={{ letterSpacing: "0.16em", background: C.paper, color: C.forest }}
                    >
                      {r}
                    </span>
                  ))}
                </div>

                {/* Author */}
                <figcaption
                  className="mt-6 pt-5 border-t flex items-center gap-3"
                  style={{ borderColor: C.hairline }}
                >
                  <div
                    className="w-11 h-11 flex items-center justify-center text-sm font-extrabold flex-shrink-0"
                    style={{ background: C.forest, color: C.paper }}
                  >
                    {t.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: C.forest }}>{t.name}</p>
                    <p className="text-xs truncate" style={{ color: C.grid }}>
                      {t.role} · {t.company}
                    </p>
                    <TechLabel style={{ color: `${C.grid}99` }}>{t.size}</TechLabel>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="relative border p-10 md:p-14 text-center" style={{ borderColor: "rgba(158,255,191,0.25)" }}>
            <CornerMarkers color={C.mint} />
            <div className="flex justify-center mb-5">
              <StatusBadge color={C.mint}>Try it</StatusBadge>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight">
              Be the next story.
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "rgba(247,247,245,0.75)" }}>
              Two-week pilots with full hand-holding. We'll set up a sandbox on your real data.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <button type="button" className="ds-skeu-btn-mint cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start a pilot
                </button>
              </Link>
              <Link to={createPageUrl("Pricing")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#9EFFBF] hover:text-[#1A3C2B] transition-colors duration-150"
                  style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
                >
                  See pricing
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
